/**
 * Inventory — design/PROTOTYPE-SPEC.md §4.
 *
 * "Same table full-page: name/brand, category, classification, qty, batch
 * status, value, actions. Sortable columns."
 *
 * Sorting is done by the API (`GET /products?sort=&direction=`) rather than in
 * the browser, because the sort keys include earliest expiry, which is derived
 * from batches the browser never sees. Sorting a derived column client-side
 * would mean re-deriving it — a second implementation of exactly the rule that
 * has bitten this codebase three times.
 */
import { useCallback, useEffect, useState } from 'react';
import { listProducts, type ProductRow } from '../lib/api.js';
import { c, field, sans } from '../theme.js';
import { Pill, cardStyle, formatDate, money, smallButton } from './format.js';
import { ProductDrawer } from './ProductDrawer.js';

type Sort = 'name' | 'qty' | 'value' | 'expiry' | 'category';
type Direction = 'asc' | 'desc';

const COLUMNS: { key: Sort | null; label: string; align?: 'right' }[] = [
  { key: 'name', label: 'Product' },
  { key: 'category', label: 'Category' },
  { key: 'qty', label: 'Qty' },
  { key: null, label: 'Stock' },
  { key: 'expiry', label: 'Expiry' },
  { key: 'value', label: 'Value', align: 'right' },
];

export function InventoryPage({
  onChanged,
  refreshToken,
}: {
  onChanged: () => void;
  refreshToken: number;
}) {
  const [rows, setRows] = useState<ProductRow[]>([]);
  const [sort, setSort] = useState<Sort>('name');
  const [direction, setDirection] = useState<Direction>('asc');
  const [search, setSearch] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ sort, direction });
      if (search.trim()) params.set('q', search.trim());
      const res = await listProducts(`?${params.toString()}`);
      setRows(res.products);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load your inventory');
    } finally {
      setLoading(false);
    }
    // refreshToken is not read here — it is a dependency so that a write from
    // the shell ("Add stock") refetches this table.
  }, [sort, direction, search, refreshToken]);

  useEffect(() => {
    // Debounced so typing in the search box does not fire a request per keystroke.
    const t = setTimeout(() => void load(), search ? 250 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  function toggleSort(key: Sort) {
    if (sort === key) {
      setDirection((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSort(key);
      setDirection('asc');
    }
  }

  const totalValue = rows.reduce((n, r) => n + r.stockValuePence, 0);

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
          marginBottom: 16,
          flexWrap: 'wrap',
        }}
      >
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, brand or category"
          style={{ ...field, maxWidth: 340 }}
        />
        <span style={{ fontSize: 13, color: c.muted }}>
          {rows.length} product{rows.length === 1 ? '' : 's'} · {money(totalValue)}
        </span>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            background: c.dangerBg,
            border: `1px solid ${c.dangerBorder}`,
            borderRadius: 12,
            padding: '12px 16px',
            marginBottom: 16,
            fontSize: 13.5,
            color: c.danger,
          }}
        >
          {error}
        </div>
      )}

      {loading ? (
        <p style={{ color: c.muted, fontSize: 14 }}>Loading…</p>
      ) : rows.length === 0 ? (
        <div style={cardStyle}>
          <p style={{ margin: 0, fontSize: 13.5, color: c.muted }}>
            {search.trim()
              ? `Nothing matches “${search.trim()}”.`
              : 'No products yet. Use Add stock to book your first delivery in, or import a spreadsheet.'}
          </p>
        </div>
      ) : (
        <div data-tour="table" style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
              <thead>
                <tr>
                  {COLUMNS.map((col) => (
                    <th
                      key={col.label}
                      style={{
                        textAlign: col.align ?? 'left',
                        padding: 0,
                        fontSize: 12.5,
                        textTransform: 'lowercase',
                        color: c.label,
                        fontWeight: 400,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {col.key ? (
                        <button
                          type="button"
                          onClick={() => toggleSort(col.key as Sort)}
                          style={{
                            width: '100%',
                            background: 'transparent',
                            border: 'none',
                            padding: '11px 16px',
                            textAlign: col.align ?? 'left',
                            cursor: 'pointer',
                            font: 'inherit',
                            color: sort === col.key ? c.ink : c.label,
                            letterSpacing: 'inherit',
                            textTransform: 'inherit',
                          }}
                        >
                          {col.label}
                          {sort === col.key && (direction === 'asc' ? ' ↑' : ' ↓')}
                        </button>
                      ) : (
                        <span style={{ display: 'block', padding: '11px 16px' }}>{col.label}</span>
                      )}
                    </th>
                  ))}
                  <th style={{ padding: '11px 16px' }} />
                </tr>
              </thead>
              <tbody>
                {rows.map((p) => (
                  <tr
                    key={p.id}
                    onClick={() => setOpenId(p.id)}
                    style={{ borderTop: `1px solid ${c.border}`, cursor: 'pointer' }}
                  >
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ fontWeight: 500 }}>{p.name}</div>
                      {p.brand && (
                        <div style={{ fontSize: 12, color: c.muted, marginTop: 2 }}>{p.brand}</div>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', color: c.muted2 }}>
                      {p.category ?? '—'}
                      {p.classificationLabel && (
                        <div style={{ fontSize: 12, color: c.faint, marginTop: 2 }}>
                          {p.classificationLabel}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                      {p.qtyOnHand}
                      <span style={{ color: c.faint }}> / min {p.minLevel}</span>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <Pill tone={p.status.stock}>{p.status.stockLabel}</Pill>
                    </td>
                    <td style={{ padding: '12px 16px', whiteSpace: 'nowrap' }}>
                      <Pill tone={p.status.expiry}>{p.status.expiryLabel}</Pill>
                      {p.earliestExpiry && (
                        <div style={{ fontSize: 11.5, color: c.faint, marginTop: 3 }}>
                          {formatDate(p.earliestExpiry)}
                        </div>
                      )}
                    </td>
                    <td
                      style={{ padding: '12px 16px', textAlign: 'right', whiteSpace: 'nowrap' }}
                    >
                      {money(p.stockValuePence)}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right' }}>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setOpenId(p.id);
                        }}
                        style={{ ...smallButton, fontFamily: sans }}
                      >
                        Open
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {openId && (
        <ProductDrawer
          productId={openId}
          onClose={() => setOpenId(null)}
          onChanged={async () => {
            await load();
            onChanged();
          }}
        />
      )}
    </>
  );
}
